import { QueryCache, QueryClient, QueryKey, QueryObserver } from "@tanstack/react-query";
import { Duration } from "date-fns";
import { queryKeyHashFn } from "./queryKeyHashFn";
import { durationToMilliseconds } from "@silo/utils";

type FreezeQueryCache = (params: {
  window: Window;
  queryKey: QueryKey;
  value: unknown;
  queryClient: QueryClient;
  queryCache: QueryCache;
  activeFreezeMap: ActiveFreezeMap;
  duration: Duration;
}) => void;

export type ActiveFreezeMap = Map<string, { frozenValue: unknown; destroyEarly: () => void }>;

/**
 * Freezes the cache value of `queryKey` to the `value` for the specified
 * `duration`
 */
export const freezeQueryCache: FreezeQueryCache = ({
  window,
  queryKey,
  queryCache,
  value: frozenValue,
  queryClient,
  activeFreezeMap,
  duration,
}) => {
  const queryHash = queryKeyHashFn(queryKey);

  // set cache value to the desired "frozen" value
  queryCache.find(queryKey)?.setData(frozenValue);

  /**
   * If there is an active freeze happening, then destroy it so that we don't
   * end up with multiple subscriptions that mutate the cache.
   */
  const activeFreeze = activeFreezeMap.get(queryHash);

  if (activeFreeze) {
    activeFreeze.destroyEarly();
    activeFreezeMap.delete(queryHash);
  }

  /**
   * Set up a subscription to the query key's associated query. Whenever the
   * data changes, set it back to the frozen value.
   *
   * Creating a new observer also ensures that the query is not garbage
   * collected during the duration.
   */
  const observer = new QueryObserver(queryClient, { queryKey, notifyOnChangeProps: ["data"] });

  observer.subscribe(() => {
    /**
     * TODO: may need to check for data equality first, but it seems like
     * react-query's structural sharing prevents an infinite set/notify loop.
     */
    queryCache.find(queryKey)?.setData(frozenValue);
  });

  const timeout = window.setTimeout(() => {
    observer.destroy();
    activeFreezeMap.delete(queryHash);
    queryClient.invalidateQueries({ queryKey });
  }, durationToMilliseconds(duration));

  activeFreezeMap.set(queryHash, {
    frozenValue,
    destroyEarly() {
      observer.destroy();
      window.clearTimeout(timeout);
    },
  });
};
